import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productApi } from '../../../services/productApi';
import { toPersianNumbers } from "../../../utils/toPersianNumbers";
import ImageGallery from "../../../components/ProductManagement/ImageGallery";
import Button from "../../../components/Button";

const SupplierPDP = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [productData, setProductData] = useState(null);
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // داده‌های ثابت
  const staticData = {
    rating: 4.2,
    supplier: "سنگبری آذرخش",
    reviewsCount: 128,
    warranty: "18 ماه گارانتی",
    brand: "برند نمونه",
    sku: "PRD-2024-001",
    deliveryTime: "ارسال 24 ساعته",
  };

  const UNIT_TRANSLATIONS = {
  'count': 'تعداد',
  'package': 'بسته',
  'weight': 'کیلو گرم',
  'length': 'متر',
  'area': 'متر مربع',
  'litr': 'لیتر',
  'volume': 'متر مکعب'
};

  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      // دریافت اطلاعات محصول
      const productResponse = await productApi.get(id);
      setProductData(productResponse.data);

      // اگر دسته‌بندی داشت، مشخصه‌های آن را بگیر
      if (productResponse.data.category?.id) {
        fetchCategoryAttributes(productResponse.data.category.id);
      }
    } catch (err) {
      setError('خطا در دریافت اطلاعات محصول');
      console.error('Error fetching product details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryAttributes = async (categoryId) => {
    try {
      const response = await productApi.getAttributes(categoryId);
      setCategoryAttributes(response.data || []);
    } catch (err) {
      console.error('Error fetching category attributes:', err);
    }
  };

  // رندر امن مقدارها
  const renderValue = (value) => {
    if (value === null || value === undefined) return "ثبت نشده";
    if (typeof value === "object") {
      if (value.value !== undefined) return value.value;
      if (value.name !== undefined) return value.name;
      if (value.title !== undefined) return value.title;
      return JSON.stringify(value);
    }
    return value;
  };

  // ساخت لیست یکپارچه ویژگی‌ها از attribute_values + custom_attributes
  const buildAttributesList = () => {
    const list = [];

    // ویژگی‌های دسته‌ای (از API)
    if (productData.attribute_values && Array.isArray(productData.attribute_values)) {
      productData.attribute_values.forEach((attr) => {
        list.push({
          label: attr.attribute_name || attr.attribute?.name,
          value: renderValue(attr.value),
          unit: attr.attribute?.unit || attr.attribute_unit,
          is_featured: attr.is_featured || false,
        });
      });
    }

    // ویژگی‌های سفارشی
    if (Array.isArray(productData.custom_attributes)) {
      productData.custom_attributes.forEach((attr, idx) => {
        list.push({
          label: attr.title || `ویژگی ${idx + 1}`,
          value: `${renderValue(attr.value)}${attr.unit ? " " + attr.unit : ""}`,
          is_featured: attr.is_featured || false,
        });
      });
    }

    return list;
  };

  // آدرس تصویر
  const getImageUrl = (img) => {
    if (!img) return "";

    // اگر از API می‌آید و object است
    if (typeof img === 'object') {
      if (img.url) return img.url;
      if (img.image) return img.image; // برای ProductImage objects
    }

    // اگر string است
    if (typeof img === "string") return img;

    // اگر File/Blob است
    if (typeof File !== "undefined" && img instanceof File) {
      return URL.createObjectURL(img);
    }
    if (typeof Blob !== "undefined" && img instanceof Blob) {
      return URL.createObjectURL(img);
    }

    return "";
  };

  // تبدیل images از API به فرمت مورد نیاز ImageGallery
  const formatImagesForGallery = () => {
  if (!productData?.images) return [];

  return productData.images.map(img => ({
    id: img.id,
    order: img.order,
    // استفاده از image_url اگر وجود دارد، در غیر این صورت image
    url: img.image_url || img.image || '',
    thumbnail_url: img.thumbnail_url || img.image_url || img.image || '',
    is_featured: img.is_featured || false,
  }));
};

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600">در حال دریافت اطلاعات محصول...</p>
      </div>
    );
  }

  if (error || !productData) {
    return (
      <div className="p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">{error || 'محصول یافت نشد'}</h3>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          بازگشت به لیست
        </button>
      </div>
    );
  }

  const allAttributes = buildAttributesList();
  const topAttributes = allAttributes.filter(attr => attr.is_featured);
  const hasAnyAttribute = allAttributes.length > 0;

  const isProductComplete = productData.name && productData.category && productData.price;

  const categoryName = productData.category_full_path

  const imagesForGallery = formatImagesForGallery();

  return (
    <div className="p-6">
      {/* دکمه‌های اکشن بالای صفحه */}
      <div className="mb-6 flex justify-between items-center">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          بازگشت
        </button>

        <div className="flex items-center space-x-4 space-x-reverse">
          <Button variant={'primary'} children={'ویرایش محصول'} onClick={() => navigate(`/supplier-panel/supplier-product-edit/${productData.id}`)}/>

          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            productData.status === 'approved' ? 'bg-green-100 text-green-800' :
            productData.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
            productData.status === 'draft' ? 'bg-gray-100 text-gray-400' :
            productData.status === 'editing' ? 'bg-gray-100 text-gray-800' :
            'bg-red-100 text-red-800'
          }`}>
            {productData.status === 'approved' ? 'تأیید شده' :
             productData.status === 'pending' ? 'در انتظار تأیید' :
             productData.status === 'draft' ? 'پیش‌نویس' :
             productData.status === 'editing' ? 'نیاز به اصلاح' :
             'رد شده'}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <main className="w-full lg:w-3/4">
          {/* ستون تصویر و اطلاعات */}
          <div className="grid lg:grid-cols-2 gap-6 items-start mb-6">
            {/* گالری تصاویر */}
            <div>
              <ImageGallery
                images={imagesForGallery}
                productName={productData.name}
              />
            </div>

            {/* عنوان و اطلاعات محصول */}
            <div className="order-2 lg:order-2 mb-6 text-center lg:text-right">
              <h1 className="text-[20px] text-[#1E3A5F] font-semibold">
                {productData.name || "نام محصول ثبت نشده"}
              </h1>
              <div className="mt-1 text-sm text-gray-600">
                {productData.brand || staticData.brand} / {categoryName}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {staticData.supplier} • امتیاز {toPersianNumbers(staticData.rating)} از {toPersianNumbers(staticData.reviewsCount)} نظر
              </div>

              <h2 className="mt-[24px] text-base font-bold text-gray-900 mb-3">ویژگی‌ها</h2>
              {topAttributes.length > 0 ? (
                <ul className="space-y-1 text-sm text-gray-800">
                  {topAttributes.map((attr, idx) => (
                    <li key={idx}>
                      {attr.label} : <span className="font-medium">{toPersianNumbers(attr.value)}</span>
                      {attr.unit && <span className="font-medium"> {toPersianNumbers(attr.unit)}</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-400">هیچ ویژگی‌ای ثبت نشده است.</p>
              )}
            </div>
          </div>

          {/* بخش توضیحات */}
          <section className="mb-8">
            <h2 className="text-base font-bold text-gray-900 mb-3">توضیحات</h2>
            <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-5 text-sm leading-relaxed text-gray-700">
              {productData.description ? (
                productData.description.split("\n").map((line, index) => (
                  <p key={index} className={line ? "mb-2" : "mb-4"}>
                    {line || <br />}
                  </p>
                ))
              ) : (
                <p className="text-gray-400">هیچ توضیحی ثبت نشده است.</p>
              )}
            </div>
          </section>

          {/* تمام ویژگی‌ها */}
          {hasAnyAttribute && (
              <section className="mt-4">
                <h2 className="mt-[24px] text-base font-bold text-gray-900 mb-3">ویژگی‌ها</h2>
                <div className="space-y-1 text-sm text-gray-800">
                  {allAttributes.map((attr, idx) => (
                      <div key={idx}>
                        {attr.label} : <span className="font-medium">{toPersianNumbers(attr.value)}</span>
                        {attr.unit && <span className="font-medium"> {toPersianNumbers(attr.unit)}</span>}
                      </div>
                  ))}
                </div>
                <div className="space-y-1 text-sm text-gray-800">
                  <div>وزن (کیلوگرم) : <span className="font-medium">{toPersianNumbers(productData.weight)}</span></div>
                  <div>عرض (سانتی متر) : <span className="font-medium">{toPersianNumbers(productData.width)}</span></div>
                  <div>طول (سانتی متر) : <span className="font-medium">{toPersianNumbers(productData.length)}</span></div>
                  <div>ارتفاع (سانتی متر) : <span className="font-medium">{toPersianNumbers(productData.height)}</span></div>
                </div>
              </section>
          )}
        </main>

        {/* ستون سمت چپ */}
        <aside className="w-full lg:w-1/4 space-y-8">
          {/* اطلاعات تأمین‌کننده و قیمت */}
          <div>
            <h3 className="text-base mb-2 text-[#1E3A5F] font-semibold">
              {staticData.supplier || "نام تأمین کننده"}
            </h3>
            <div className="my-2 text-xs text-gray-500">
              امتیاز {toPersianNumbers(staticData.rating)} از {toPersianNumbers(staticData.reviewsCount)} نظر
            </div>
          </div>

          {/* روش‌های ارسال */}
          <div>
            <h3 className="mb-4 text-[18px] text-[#1E3A5F] font-semibold">روش‌های ارسال</h3>
            <ul className="space-y-1 text-sm text-gray-800">
              {productData.delivery_by_supplier && (
                <li>ارسال توسط تأمین کننده</li>
              )}
              {productData.delivery_by_customer && (
                <li>تحویل توسط مشتری</li>
              )}
              {productData.delivery_by_carrier && (
                <li>ارسال توسط شرکت حمل</li>
              )}
              {!productData.delivery_by_supplier &&
                !productData.delivery_by_customer &&
                !productData.delivery_by_carrier && (
                  <li className="text-gray-400">روش ارسال ثبت نشده است</li>
                )}
            </ul>
          </div>

          {/* خلاصه وضعیت محصول */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">وضعیت کالا</span>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  isProductComplete
                    ? "bg-green-100 text-green-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {isProductComplete ? "تقریباً کامل" : "ناقص"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">تعداد عکس‌ها</span>
              <span className="font-medium text-gray-900">
                {toPersianNumbers(productData.images?.length || 0)}/{toPersianNumbers(6)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">تعداد ویژگی‌ها</span>
              <span className="font-medium text-gray-900">
                {toPersianNumbers(allAttributes.length)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">تاریخ ایجاد</span>
              <span className="font-medium text-gray-900">
                {new Date(productData.created_at).toLocaleDateString('fa-IR')}
              </span>
            </div>
          </div>

          {/* زمان تحویل */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-bold text-gray-900 mb-2">زمان تحویل</h3>
            <div className="flex items-center gap-3">
              <div className="bg-blue-50 p-3 rounded-xl">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {toPersianNumbers(staticData.deliveryTime)}
                </div>
                <div className="text-xs text-gray-600">در شهرهای اصلی</div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SupplierPDP;